import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useDevicePerformance } from '../hooks/useDevicePerformance';

export type TabId = 'transcript' | 'ttml' | 'chat' | 'agent' | 'readable' | 'narrative' | 'description' | 'context' | 'questions';

interface TabConfig {
  id: TabId;
  title: string;
  icon: React.ReactNode;
  component: React.ComponentType<any>;
  props?: any;
}

interface TabLayoutProps {
  tabs: TabConfig[];
  isDarkMode: boolean;
  defaultTab?: TabId;
  activeTab?: TabId;
  onTabChange?: (tabId: TabId) => void;
  hideNavigation?: boolean;
}

const TabLayout: React.FC<TabLayoutProps> = ({
  tabs,
  isDarkMode,
  defaultTab = 'ttml',
  activeTab: controlledActiveTab,
  onTabChange,
  hideNavigation = false
}) => {
  const { isCompactTouchLayout } = useDevicePerformance();
  const [internalActiveTab, setInternalActiveTab] = useState<TabId>(defaultTab);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Partial<Record<TabId, HTMLButtonElement | null>>>({});
  const activeTab = controlledActiveTab || internalActiveTab;
  const navSurfaceClassName = isDarkMode
    ? 'border-[#FFB52B]/20 bg-[#232E3F]/[0.94] shadow-[0_22px_40px_-30px_rgba(10,14,20,0.68)]'
    : 'border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(244,244,244,0.96))] shadow-[0_20px_36px_-30px_rgba(35,46,63,0.22)]';
  const navScrollButtonClassName = isDarkMode
    ? 'absolute z-10 -translate-y-1/2 rounded-full border border-white/[0.12] bg-white/[0.06] p-2 text-[#F4F4F4] shadow-[0_12px_28px_-18px_rgba(10,14,20,0.9)] transition-all hover:border-[#FFB52B]/40 hover:bg-[#FFB52B] hover:text-[#232E3F]'
    : 'absolute z-10 -translate-y-1/2 rounded-full border border-[#D6DCE4] bg-white/[0.94] p-2 text-[#2A3649] shadow-[0_12px_28px_-20px_rgba(35,46,63,0.26)] transition-all hover:border-[#FFB52B]/45 hover:bg-[#FFB52B] hover:text-[#232E3F]';
  const navFadeLeftClassName = isDarkMode
    ? 'bg-gradient-to-r from-[#232E3F] via-[#232E3F]/90 to-transparent'
    : 'bg-gradient-to-r from-[#F4F4F4] via-[#F4F4F4]/92 to-transparent';
  const navFadeRightClassName = isDarkMode
    ? 'bg-gradient-to-l from-[#232E3F] via-[#232E3F]/90 to-transparent'
    : 'bg-gradient-to-l from-[#F4F4F4] via-[#F4F4F4]/92 to-transparent';
  const activeTabClassName = 'bg-[#FFB52B] text-[#232E3F] shadow-[0_18px_34px_-22px_rgba(255,181,43,0.9)]';
  const inactiveTabClassName = isDarkMode
    ? 'text-[#D1D8E0] hover:bg-white/[0.08] hover:text-[#F4F4F4]'
    : 'text-[#485568] hover:bg-[#232E3F]/[0.06] hover:text-[#232E3F]';

  const updateScrollState = useCallback(() => {
    const navElement = navScrollRef.current;
    if (!navElement) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const maxScrollLeft = navElement.scrollWidth - navElement.clientWidth;
    setCanScrollLeft(navElement.scrollLeft > 8);
    setCanScrollRight(maxScrollLeft - navElement.scrollLeft > 8);
  }, []);

  useEffect(() => {
    if (hideNavigation) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const navElement = navScrollRef.current;
    if (!navElement) {
      return;
    }

    updateScrollState();

    const handleResize = () => updateScrollState();
    navElement.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      navElement.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', handleResize);
    };
  }, [hideNavigation, tabs, updateScrollState]);

  useEffect(() => {
    const activeButton = tabButtonRefs.current[activeTab];
    activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    const frame = window.requestAnimationFrame(updateScrollState);
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, updateScrollState]);

  const scrollTabsBy = (direction: 'left' | 'right') => {
    const navElement = navScrollRef.current;
    if (!navElement) return;

    const distance = Math.max(navElement.clientWidth * 0.45, 180);
    navElement.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  };
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Tab Navigation - Hidden in fullscreen and on mobile */}
      {!hideNavigation && !isCompactTouchLayout && (
        <div className={`relative min-w-0 flex-shrink-0 border-b ${navSurfaceClassName}`}>
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollTabsBy('left')}
              className={`left-3 top-1/2 ${navScrollButtonClassName}`}
              aria-label="Scroll tabs left"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <div className={`pointer-events-none absolute inset-y-0 left-0 w-20 ${navFadeLeftClassName}`} />
          <div className={`pointer-events-none absolute inset-y-0 right-0 w-20 ${navFadeRightClassName}`} />
          <div
            ref={navScrollRef}
            className="flex min-w-0 gap-2 overflow-x-auto scrollbar-hide scroll-smooth px-14 py-3"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                ref={(node) => {
                  tabButtonRefs.current[tab.id] = node;
                }}
                onClick={() => {
                  if (onTabChange) {
                    onTabChange(tab.id);
                  } else {
                    setInternalActiveTab(tab.id);
                  }
                }}
                className={`relative flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2.5 font-headlines text-sm font-semibold transition-all md:px-5 ${
                  activeTab === tab.id
                    ? activeTabClassName
                    : inactiveTabClassName
                }`}
              >
                {tab.icon}
                <span className="text-sm md:text-[0.95rem]">{tab.title}</span>
              </button>
            ))}
          </div>
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollTabsBy('right')}
              className={`right-3 top-1/2 ${navScrollButtonClassName}`}
              aria-label="Scroll tabs right"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      )}

      {/* Tab Content - All tabs are mounted but only active one is visible */}
      <div className={`flex-1 min-h-0 overflow-hidden ${
        isDarkMode ? 'bg-[#16202C]/[0.92]' : 'bg-[#F8F8F7]/[0.86]'
      }`}>
        {tabs.map((tab) => {
          const TabComponent = tab.component;
          const isActive = activeTab === tab.id;

          return (
            <div
              key={tab.id}
              className={`h-full overflow-y-auto ${isActive ? 'block' : 'hidden'}`}
            >
              <TabComponent {...(tab.props || {})} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TabLayout;
